import * as babylon from "@babylonjs/core";
import lodash from "lodash";

import * as schema from "@/shared/state/visualization/schema/v1/three-d";

import { recolorMaterial } from "../../assetBuilders/materials";
import { BabylonState } from "../../state";

export function updateSceneConfig(
  state: BabylonState,
  spec: schema.PanelConfig | undefined,
) {
  spec ??= {};
  syncCamera(state, spec.camera);
  syncGrid(state, spec.grid);
  return state.sceneConfig;
}

function syncCamera(state: BabylonState, spec: schema.Camera | undefined) {
  spec ??= {
    type: "orbit",
    zoom: { min: 1, max: 100 },
    target: {
      id: Object.values(state.entities)[0]?.id ?? schema.DEFAULT_ENTITY_ID,
      mesh: Object.values(state.entities)[0]?.mesh ? "mesh" : "path",
    },
  };
  // Short-circuit if the config is unchanged.
  if (lodash.isEqual(spec, state.sceneConfig.camera?.spec)) {
    return;
  }
  // TODO: it's possible to compute the zoom range based on the bounding box of the camera's target.
  // See https://doc.babylonjs.com/features/featuresDeepDive/mesh/displayBoundingBoxes/ for more
  const targetMesh = state.entities[spec.target.id]?.[spec.target.mesh]?.mesh;
  const zoom = {
    min: spec.zoom?.min ?? 1,
    max: spec.zoom?.max ?? 100,
  };

  let camera = state.sceneConfig.camera;
  if (!camera) {
    camera = {
      spec: spec,
      zoom,
      babylonCamera: new babylon.ArcRotateCamera(
        `sceneConfig.camera`,
        (45 * Math.PI) / 180,
        (75 * Math.PI) / 180,
        zoom.min + (zoom.max - zoom.min) / 5,
        babylon.Vector3.Zero(),
        state.context.scene,
      ),
    };
    camera.babylonCamera.attachControl(state.context.canvas, true);
    state.sceneConfig.camera = camera;
  }

  camera.babylonCamera.minZ = (zoom.min ?? 1) / 10;
  camera.babylonCamera.wheelDeltaPercentage = 0.01;
  camera.babylonCamera.inertia = 0.95;
  camera.babylonCamera.fov = 55 * (Math.PI / 180);
  if (targetMesh) {
    camera.babylonCamera.setTarget(targetMesh);
  }
  camera.spec = spec;
}

function syncGrid(state: BabylonState, spec: schema.Grid | undefined) {
  spec ??= {
    color: "#88888888",
    scale: 5,
    verticalOffset: 0,
  };
  // Short-circuit if the config is unchanged.
  if (lodash.isEqual(spec, state.sceneConfig.grid?.spec)) {
    return;
  }
  const grid = state.sceneConfig.grid;
  // If the grid size has not changed, then the pre-render system can reposition it and we don't have to rebuild the mesh.
  if (grid && spec.scale === grid.spec.scale) {
    if (spec.color !== grid.spec.color && grid?.material) {
      recolorMaterial(grid.material, spec.color);
    }
    grid.spec = spec;
    return;
  }

  // Otherwise, dispose the old grid, and make a new one ready to be loaded.
  if (grid) {
    grid.mesh?.dispose();
    grid.material?.dispose();
    delete state.sceneConfig.grid;
  }

  state.sceneConfig.grid = {
    spec,
    verticalOffset: 0,
    load: {
      state: "stale",
    },
  };
}
